'use client'
import { Contest } from '@/types/contest'
import { Chip } from '@/components/common'
import { timeRemaining } from '@/services/utils/time'
import { cva, VariantProps } from 'class-variance-authority'
import Image from 'next/image'
import { formatBaht } from '@/services/utils/currency'
import { ShareMenu } from './share-menu.client'
import { categoryChips } from '@/hooks/api/use-categories'
import { cn } from '@/services/utils/cn'
import { markdownToPlainText } from '@/services/utils/markdown'
import { mapEducationLevelLabel } from '@/services/utils/labels'

const contestVariants = cva(
  'flex cursor-pointer items-start gap-4 rounded-xl max font-line shadow-cardcustom border border-gray-100 @container hover:shadow-sm transition-all duration-200 ease-in-out',
  {
    variants: {
      size: {
        default: 'h-[200px] px-3 py-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

function ContestCard({
  contest,
  noDropdown = false,
}: { contest: Contest; noDropdown?: boolean } & VariantProps<
  typeof contestVariants
>) {
  const date = new Date(contest.closing_date as string)

  const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
  const { text: formattedTime } = timeRemaining(contest.closing_date as string)

  return (
    <div className={cn(contestVariants({ size: 'default' }))}>
      <div className="hidden aspect-[4/5] h-full w-auto rounded-xl bg-[#808080] @md:flex">
        {contest?.poster_url && (
          <Image
            src={contest.poster_url}
            alt={`${contest.title_en} image`}
            width={480}
            height={600}
            className="h-full w-full rounded-lg object-cover"
          />
        )}
      </div>
      <div className="flex h-full w-full flex-col">
        <div className="flex w-full justify-between">
          <div className="flex w-full max-w-[240px] items-center gap-2 overflow-x-auto whitespace-nowrap pr-2 @lg:max-w-[300px] @xl:max-w-[380px] @2xl:max-w-full">
            {Array.isArray(contest.categories) &&
              contest.categories.map((category) => (
                <Chip
                  key={category.id}
                  name={category.name_en}
                  activeColor={
                    categoryChips.find(
                      (item) => item.id === String(category.id)
                    )?.activeColor
                  }
                  inactiveColor={
                    categoryChips.find(
                      (item) => item.id === String(category.id)
                    )?.inactiveColor
                  }
                  static
                  initiallyActive={true}
                />
              ))}
          </div>
          <div className="">
            {!noDropdown && <ShareMenu contest={contest} />}
          </div>
        </div>
        <div className="mt-2 flex h-[140px] w-full">
          <div className="flex aspect-[4/5] h-full w-auto rounded-xl bg-[#808080] @md:hidden">
            {contest?.poster_url && (
              <Image
                src={contest.poster_url}
                alt={`${contest.title_en} image`}
                width={480}
                height={600}
                className="h-full w-full rounded-lg object-cover"
              />
            )}
          </div>
          <div className="ml-4 flex w-full flex-col overflow-hidden @md:ml-0">
            <div className="grow">
              <h1 className="line-clamp-1 text-base font-semibold @md:mt-[20px]">
                {contest.title_th || contest.title_en}
              </h1>
              <p className="mt-[6px] line-clamp-3 whitespace-pre-wrap break-words text-[9.5px] font-normal text-[#727272]">
                {markdownToPlainText(
                  contest.description_th ||
                    contest.description_en ||
                    'ไม่มีคำอธิบาย'
                )}
              </p>
            </div>
            <div className="grid w-full grid-cols-1 text-[11px] font-normal @sm:grid-cols-2 @lg:grid-cols-4">
              <h4 className="flex gap-2">
                <span>รางวัล</span>
                <span>{formatBaht(contest.prize?.total_prize_value ?? 0)}</span>
              </h4>
              <h4 className="flex gap-2">
                <span>คุณสมบัติ</span>
                <span>
                  {(contest?.educational_level?.length ?? 0) > 0
                    ? contest.educational_level
                        .map((level) => mapEducationLevelLabel(level))
                        .join(', ')
                    : '-'}
                </span>
              </h4>
              <h4 className="flex gap-2">
                <span>ปิดรับสมัคร</span>
                {formattedDate}
              </h4>
              <h4 className="flex gap-2">
                <span>เหลือเวลา</span>
                <span className="font-bold">{formattedTime}</span>
              </h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { ContestCard, contestVariants }
